import type { OBActorType } from "@/types/workflow"
import type { TranslationKey } from "@/translations/index"

export interface OrgTypeStyle {
  color: string
  labelKey: TranslationKey
}

const RESERVED: Record<OBActorType, OrgTypeStyle> = {
  Consultant:      { color: "#3BD4AE", labelKey: "actorTypeWorker" },
  ClientService:   { color: "#FF6B00", labelKey: "actorTypeClientServices" },
  AccountManager:  { color: "#531A86", labelKey: "actorTypeAccountManager" },
  Executive:       { color: "#1E3A8A", labelKey: "actorTypeExecutive" },
  Recruiter:       { color: "#005CB9", labelKey: "actorTypeRecruiter" },
  SubvendorOrg:    { color: "#D4930A", labelKey: "actorTypeSubvendorOrg" },
  SubvendorWorker: { color: "#92400E", labelKey: "actorTypeSubvendorWorker" },
  ReadOnlyPreview: { color: "#64748B", labelKey: "actorTypeReadOnly" },
}

const RESERVED_ORDER: OBActorType[] = [
  "Consultant",
  "ClientService",
  "AccountManager",
  "Executive",
  "Recruiter",
  "SubvendorOrg",
  "SubvendorWorker",
  "ReadOnlyPreview",
]

const FALLBACK_PALETTE = [
  "#005CB9",
  "#D4930A",
  "#1A7B56",
  "#C0392B",
  "#2471A3",
  "#7D3C98",
]

function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h
}

export function getOrgTypeStyle(actorType: string): OrgTypeStyle & { fallbackLabel?: string } {
  if (actorType in RESERVED) {
    return RESERVED[actorType as OBActorType]
  }
  const color = FALLBACK_PALETTE[hashString(actorType) % FALLBACK_PALETTE.length]
  // Unknown types don't have a translation key — callers should use fallbackLabel
  return { color, labelKey: "actorTypeWorker", fallbackLabel: actorType }
}

export function buildLegendEntries(actorTypes: string[]): Array<OrgTypeStyle & { fallbackLabel?: string }> {
  const unique = [...new Set(actorTypes)]
  const reserved = RESERVED_ORDER.filter(t => unique.includes(t))
  const dynamic = unique
    .filter(t => !(t in RESERVED))
    .sort((a, b) => a.localeCompare(b))
  return [...reserved, ...dynamic].map(getOrgTypeStyle)
}
